
import React, { useEffect, useState } from 'react';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  markdownContent: string;
}

export function TableOfContents({ markdownContent }: TableOfContentsProps) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeHeading, setActiveHeading] = useState('');

  useEffect(() => {
    const extractHeadings = (content: string) => {
      const headingRegex = /^(#{1,3})\s+(.+)$/gm;
      const headings: Heading[] = [];
      let match;

      while ((match = headingRegex.exec(content)) !== null) {
        const level = match[1].length;
        const text = match[2];
        const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
        headings.push({ id, text, level });
      }

      return headings;
    };

    setHeadings(extractHeadings(markdownContent));
  }, [markdownContent]);

  useEffect(() => {
    const handleScroll = () => {
      const headingElements = headings
        .map((heading) => document.getElementById(heading.id))
        .filter(Boolean);

      const headingPositions = headingElements.map((el) => ({
        id: el!.id,
        position: el!.getBoundingClientRect().top
      }));

      const visibleHeadings = headingPositions.filter(
        ({ position }) => position < 200 && position > -200
      );

      if (visibleHeadings.length > 0) {
        setActiveHeading(visibleHeadings[0].id);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <div className="sticky top-20 p-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
      <h4 className="text-sm font-semibold mb-3">On this page</h4>
      <nav className="space-y-1">
        {headings.map((heading) => (
          <a
            key={heading.id}
            href={`#${heading.id}`}
            className={`
              pl-${(heading.level - 1) * 3} block text-sm py-1 border-l-2 px-3 cursor-pointer
              ${activeHeading === heading.id ? 'border-primary text-primary font-medium' : 'border-transparent hover:border-border hover:text-foreground'}
              transition-all
            `}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
              setActiveHeading(heading.id);
            }}
          >
            {heading.text}
          </a>
        ))}
      </nav>
    </div>
  );
}
